import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import TablePagination from "../../table/table-pagination/TablePagination";
import { convertDateToLocalTimeWithHour, notify } from "../../../assets/utils/UtilsFunctons";
import Enums from "../../../assets/JsonData/Enums";
import PepUserActionRowOffCanvas from "./PepUserActionRowOffCanvas";
import PepDataService from "../../../services/PepDataService";
import PropTypes from "prop-types";
import ConfirmContextProvider from "../../FormConFirmDialog/FormConfirmContextProvider";
import ConfirmModal from "../../modals/FormConfirmDialogModal";
import useUserRole from "../../../hooks/useUserRole";

const PepUserActionTable = ({ pepActions, onDataReload, pageIndex }) => {
  const { isAdmin, hasAccessAsDeveloper, isManager } = useUserRole();
  const { accounts } = useMsal();
  const { usersInfos } = useSelector((state) => ({ ...state.userInfos }));

  const [isUpdating, setIsUpdating] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(pageIndex);
  const [currentAnchor, setCurrentAnchor] = useState(window.location.hash.substring(1));

  useEffect(() => {
    const handleHashChange = () => {
      setCurrentAnchor(window.location.hash.substring(1));
    };

    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  const filteredUsers = useMemo(() => {
    const users = usersInfos?.data ?? [];
    if (isManager && !isAdmin && !hasAccessAsDeveloper) {
      const oid = accounts[0]?.idTokenClaims?.oid;
      return users.filter((u) => u.operatingManagerId === oid);
    }
    return users;
  }, [usersInfos, isManager, isAdmin, hasAccessAsDeveloper, accounts]);

  // Retrouve la société du collaborateur associé à une action PEP,
  // en priorité via userId, sinon via le nom complet en secours.
  const userCompanyById = useMemo(() => {
    const allUsers = usersInfos?.data ?? [];
    return new Map(allUsers.map((u) => [u.userId, u.company]));
  }, [usersInfos]);

  const userCompanyByFullName = useMemo(() => {
    const allUsers = usersInfos?.data ?? [];
    return new Map(allUsers.map((u) => [`${u.firstName} ${u.lastName}`, u.company]));
  }, [usersInfos]);

  const getUserCompanyForAction = useCallback(
    (pepAction) => {
      if (pepAction.userId) {
        return userCompanyById.get(pepAction.userId) ?? null;
      }
      return userCompanyByFullName.get(pepAction.userFullName) ?? null;
    },
    [userCompanyById, userCompanyByFullName]
  );

  // Enrichit les pepActions avec un champ "company" exploitable
  // pour la colonne et le filtre du tableau.
  const enrichedPepActions = useMemo(() => {
    return pepActions.map((action) => ({
      ...action,
      company: getUserCompanyForAction(action),
    }));
  }, [pepActions, getUserCompanyForAction]);

  const getCompaniesList = useCallback(() => {
    return [
      { value: 0, label: "Diggers" },
      { value: 1, label: "Finaura" },
    ];
  }, []);

  const companyDisplayValueFunc = useCallback((company) => {
    if (company == null) return "-";
    if (typeof company === "object" && company.label) return company.label;
    return Enums.matchCompany(company);
  }, []);

  const toggle = () => {
    window.history.pushState("", document.title, window.location.pathname + window.location.search);
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  };

  const columns = [
    {
      Header: "Collaborateur",
      accessor: "userFullName",
    },
    {
      Header: "Société",
      accessor: "company",
      Cell: ({ value }) => Enums.matchCompany(value),
    },
    {
      Header: "Axe",
      accessor: "axis.title",
    },
    {
      Header: "Domaine",
      accessor: "domain.title",
    },
    {
      Header: "Compétence",
      accessor: "skill.title",
    },
    {
      Header: "Action",
      accessor: "title",
    },
    {
      Header: "Niveau",
      accessor: "level",
      Cell: ({ value }) => {
        return Enums.matchPepActionConfigLevel(value) || null;
      },
    },
    {
      Header: "Priorité",
      accessor: "priority",
      Cell: ({ value }) => {
        return Enums.matchPepActionPriority(value) || null;
      },
    },
    {
      Header: "Statut",
      accessor: "status",
      Cell: ({ value }) => {
        return Enums.matchPepActionStatus(value) || null;
      },
    },
    {
      Header: "Progres.",
      accessor: "progress",
      Cell: ({ value }) => {
        return <span> {value} %</span> || null;
      },
    },
    {
      Header: "Début",
      accessor: "startDate",
      Cell: ({ value }) => {
        return convertDateToLocalTimeWithHour(value) || null;
      },
    },
    {
      Header: "Fin",
      accessor: "endDate",
      Cell: ({ value }) => {
        return convertDateToLocalTimeWithHour(value) || null;
      },
    },
    {
      Header: "DigDays",
      accessor: "digDaysPlanned",
    },
  ];

  const tableHooks = () => {
    return "";
  };

  const handleRowClicked = (row) => {
    window.location.hash = row.original.id;
  };

  const SelectedPepAction = useMemo(() => {
    return enrichedPepActions.find((pep) => pep.id === currentAnchor);
  }, [currentAnchor, enrichedPepActions]);

  const isOffCanvasVisible = useMemo(() => {
    return !!SelectedPepAction;
  }, [SelectedPepAction]);

  const onUpdateRow = useCallback(
    async ({ id, payload, successMessage = undefined }) => {
      try {
        setIsUpdating(true);
        await PepDataService.editPepAction(id, payload);
        setIsUpdating(false);
        notify(1, successMessage ?? "Opération effectuée avec succès !");
        await new Promise((resolve) => setTimeout(resolve, 300));
        onDataReload(currentPageIndex);
      } catch (error) {
        notify(0, error?.response.data.title ?? "une erreur est survenue");
      } finally {
        setIsUpdating(false);
      }
    },
    [currentPageIndex]
  );

  const renderPepOffCanvaDetail = useCallback(() => {
    return (
      <PepUserActionRowOffCanvas
        isVisible={isOffCanvasVisible}
        selectedRow={SelectedPepAction}
        toggle={toggle}
        onUpdateRow={(args) => onUpdateRow(args)}
        onDataReload={onDataReload}
        currentPageIndex={currentPageIndex}
        isLoading={isUpdating}
      />
    );
  }, [isOffCanvasVisible, SelectedPepAction, isUpdating, currentPageIndex]);

  const userFullNameDisplayValueFunc = useCallback(
    (userFullName) => {
      const user = filteredUsers.find(
        (u) => `${u.firstName} ${u.lastName}` === userFullName
      );
      return user ? `${user.firstName} ${user.lastName}` : null;
    },
    [filteredUsers]
  );

  return (
    <div>
      <ConfirmContextProvider>
        <ConfirmModal />
        <TablePagination
          data={enrichedPepActions}
          columns={columns}
          tableHooks={tableHooks}
          refresh={false}
          handleRowClicked={handleRowClicked}
          type={"default"}
          enableFilter={true}
          hasDefaultValue={false}
          onPageIndexChange={(indexPage) => setCurrentPageIndex(indexPage)}
          initialPageIndex={currentPageIndex}
        >
          <element
            key="userFullName"
            filterKey="userFullName"
            className="cell"
            casesensitive={"true"}
            showsearch={"true"}
            itemDisplayValueFunc={userFullNameDisplayValueFunc}>
            Collab..
          </element>
          <element
            key="company"
            filterKey="company"
            className="cell"
            casesensitive={"true"}
            showsearch={"true"}
            data={getCompaniesList()}
            itemDisplayValueFunc={companyDisplayValueFunc}
          >
            Société
          </element>
          <element key="axis" filterKey="axis" isObjectKey={true} subKey={"title"} className="cell" casesensitive={"true"} showsearch={"true"}>
            Axe
          </element>
          <element key={"domain"} filterKey={"domain"} isObjectKey={true} subKey={"title"} className="cell" casesensitive={"true"} showsearch={"true"}>
            Domaine
          </element>
          <element key="skill" filterKey="skill" isObjectKey={true} subKey={"title"} className="cell" casesensitive={"true"} showsearch={"true"}>
            Compétence
          </element>
        </TablePagination>
        {renderPepOffCanvaDetail()}
      </ConfirmContextProvider>
    </div>
  );
};

PepUserActionTable.propTypes = {
  pepActions: PropTypes.array.isRequired,
  onDataReload: PropTypes.func,
  pageIndex: PropTypes.number,
};

export default PepUserActionTable;
